import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { BASE_URL_API } from '../Constants';
import Spinner from '../Components/Spinner';
import Alert from '../Components/Alert';
import { LanguageContext } from '../context/LanguageContext';
import assetTranslations from '../languages/asset.json';
import { useAuth } from "../hooks/AuthProvider";
import moment from 'moment';
import './AssetHistoryPage.css';
import './details.css';
import {
    AssetCardMainIcon,
    PowerIcon,
    VoltageIcon,
    EfficiencyIcon
} from '../svg/AssetCardIcon';

import { Select } from '@mui/material';

const formatDateTime = (dateValue) =>
    moment(dateValue).isValid()
        ? moment(dateValue).format('DD/MM/YYYY HH:mm:ss')
        : '-';

const interpolateText = (template, values = {}) =>
    Object.entries(values).reduce(
        (result, [key, value]) =>
            result.split(`{${key}}`).join(String(value ?? '')),
        template
    );

const historyTrackedFields = {
    // AssetCardHistory
    location: 'Location',
    device_id: 'Device ID',

    // OperationalHistory
    current: 'Current',
    efficiency_percent: 'Efficiency Percent',
    running_hours_counter: 'Running Hours Counter',

    // MaintenanceHistory
    condition_status: 'Condition Status',
    technical_evaluation: 'Technical Evaluation',
    criticality_level: 'Criticality Level',
    estimated_life_cycle_years: 'Estimated Life Cycle Years',
    last_maintenance_date: 'Last Maintenance Date',
    next_maintenance_due: 'Next Maintenance Due',
    depreciation: 'Depreciation',

    // FinancialHistory
    department_owner: 'Department Owner',
    annual_om_cost: 'Annual O&M Cost',

    // IotHistory
    monitored_parameters: 'Monitored Parameters',
    alert_thresholds: 'Alert Thresholds',

    // Main Asset Card fields (for comparison)
    asset_name: 'Asset Name',
    asset_code: 'Asset Code',
    category_subcategory: 'Category/Subcategory',
    brand: 'Brand',
    model_number: 'Model Number',
    serial_number: 'Serial Number',
    type: 'Type',
    manufacturer: 'Manufacturer',
    manufacture_date: 'Manufacture Date',
    installation_date: 'Installation Date',
    warranty_expiry_date: 'Warranty Expiry Date',
    o_and_m_vendor: 'O&M Vendor',
    supplier_vendor: 'Supplier Vendor',
    purchase_cost: 'Purchase Cost',
    integration_protocol: 'Integration Protocol',
    capacity_rating: 'Capacity Rating',
    power: 'Power',
    voltage: 'Voltage',
    connection_type: 'Connection Type'
};

export const AssetHistoryPage = () => {
    const { id: assetId } = useParams();
    const navigate = useNavigate();
    const { lang } = useContext(LanguageContext);
    const tRoot = assetTranslations[lang] || assetTranslations.en;
    const assetT = tRoot.assetDetails;
    const addAssetT = tRoot.addAsset;
    const historyT = tRoot.historyPage;
    const isRtl = ['ar', 'ur'].includes(lang);
    const { org } = useAuth();
    const historyFieldLabels = {
        ...historyTrackedFields,
        ...(addAssetT?.validation?.labels || {})
    };

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });
    const showAlert = useCallback(
        (type, message) => {
            setAlertState({
                open: true,
                type,
                message,
                position: isRtl ? 'left' : 'right'
            });
        },
        [isRtl]
    );

    const [loading, setLoading] = useState(true);
    const [availableYears, setAvailableYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [assetSnapshot, setAssetSnapshot] = useState(null);
    const [allHistoryRecords, setAllHistoryRecords] = useState(null);
    const [showAllHistoryMode, setShowAllHistoryMode] = useState(false);
    const [showComparisonMode, setShowComparisonMode] = useState(false);
    const [selectedComparisonYears, setSelectedComparisonYears] = useState([]);
    const [selectedComparisonFields, setSelectedComparisonFields] = useState(
        []
    );
    const [comparisonData, setComparisonData] = useState([]);
    const [isCurrentYearLive, setIsCurrentYearLive] = useState(true);
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        if (!org?.id) {
            showAlert('error', historyT.organizationIdUnavailable);
            setLoading(false);
            return;
        }
        fetchAvailableYears();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org?.id]);

    useEffect(() => {
        if (selectedYear && org?.id) {
            fetchAssetSnapshotForYear(selectedYear);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear, org?.id]);

    const fetchAvailableYears = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${BASE_URL_API}/asset-card/${assetId}/history/years`,
                {
                    params: { organization_id: org.id }
                }
            );
            if (response.data && response.data.status === 'success') {
                const years = response.data.data;
                if (!years.includes(currentYear)) {
                    years.unshift(currentYear);
                }
                years.sort((a, b) => b - a);
                setAvailableYears(years);
                if (!years.includes(selectedYear) && years.length > 0) {
                    setSelectedYear(years[0]);
                }
            } else {
                showAlert('error', assetT.failedToRetrieveHistoryYears);
            }
        } catch (err) {
            console.error('Error fetching history years:', err);
            showAlert('error', historyT.failedToFetchYears);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssetSnapshotForYear = async year => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${BASE_URL_API}/asset-card/${assetId}/history/year-snapshot`,
                {
                    params: { year, organization_id: org.id }
                }
            );
            if (response.data && response.data.status === 'success') {
                setAssetSnapshot(response.data.data);
                setIsCurrentYearLive(response.data.isCurrentYearLive);
            } else {
                showAlert('error', assetT.failedToRetrieveHistorySnapshot);
                setAssetSnapshot(null);
            }
        } catch (err) {
            console.error('Error fetching asset history snapshot:', err);
            showAlert('error', historyT.failedToFetchSnapshot);
            setAssetSnapshot(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllHistoryRecordsForYear = async year => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${BASE_URL_API}/asset-card/${assetId}/history/all-for-year`,
                {
                    params: { year, organization_id: org.id }
                }
            );
            if (response.data && response.data.status === 'success') {
                setAllHistoryRecords(response.data.data);
                setShowAllHistoryMode(true);
            } else {
                showAlert('error', assetT.failedToRetrieveAllHistory);
                setAllHistoryRecords(null);
            }
        } catch (err) {
            console.error('Error fetching all history records:', err);
            showAlert('error', historyT.failedToFetchAllHistory);
            setAllHistoryRecords(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchComparisonData = async () => {
        if (
            selectedComparisonYears.length < 1 ||
            selectedComparisonFields.length < 1
        ) {
            showAlert('warning', historyT.comparisonSelectionRequired);
            return;
        }
        try {
            setLoading(true);
            const response = await axios.get(
                `${BASE_URL_API}/asset-card/${assetId}/history/comparison`,
                {
                    params: {
                        years: selectedComparisonYears,
                        fields: selectedComparisonFields,
                        organization_id: org.id
                    },
                    paramsSerializer: params =>
                        Object.entries(params)
                            .map(([key, value]) => {
                                if (Array.isArray(value)) {
                                    return value
                                        .map(val => `${key}=${val}`)
                                        .join('&');
                                }
                                return `${key}=${value}`;
                            })
                            .join('&')
                }
            );
            if (response.data && response.data.status === 'success') {
                setComparisonData(response.data.data);
            } else {
                showAlert('error', historyT.failedComparisonData);
                setComparisonData([]);
            }
        } catch (err) {
            console.error('Error fetching comparison data:', err);
            showAlert('error', historyT.failedComparisonData);
            setComparisonData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = option => {
        setSelectedYear(option ? option.value : null);
        setShowAllHistoryMode(false);
        setShowComparisonMode(false);
    };

    const handleBackToSnapshot = () => {
        setShowAllHistoryMode(false);
        setShowComparisonMode(false);
    };

    const handleComparisonYearChange = selectedOptions => {
        setSelectedComparisonYears(
            selectedOptions ? selectedOptions.map(option => option.value) : []
        );
    };

    const handleComparisonFieldChange = selectedOptions => {
        setSelectedComparisonFields(
            selectedOptions ? selectedOptions.map(option => option.value) : []
        );
    };

    const combinedHistoryTimeline = () => {
        if (!allHistoryRecords) return [];
        const allEntries = [];
        Object.entries(allHistoryRecords).forEach(([key, records]) => {
            records.forEach(record => {
                allEntries.push({
                    ...record,
                    type: key.replace('History', ''),
                    sourceTable: key
                });
            });
        });
        return allEntries.sort(
            (a, b) => new Date(a.changed_at) - new Date(b.changed_at)
        );
    };

    const renderHistoryItem = item => {
        const changes = {};
        Object.keys(item).forEach(key => {
            if (
                [
                    'id',
                    'asset_card_id',
                    'changed_by_user_id',
                    'changed_at',
                    'created_at',
                    'updated_at',
                    'type',
                    'sourceTable'
                ].includes(key)
            ) {
                return;
            }
            if (item[key] !== null && item[key] !== undefined) {
                changes[historyFieldLabels[key] || key] = item[key];
            }
        });

        const typeKey = String(item.type || '').trim();
        const typeLabel = historyT.typeLabels?.[typeKey] || typeKey;

        return (
            <div
                className="history-timeline-item"
                key={item.id || item.changed_at}
            >
                <div className="history-timeline-dot" />
                <div className="history-timeline-content">
                    <div className="history-timeline-meta">
                        <span className="history-timeline-type">
                            {`${typeLabel} ${historyT.changeSuffix}`}
                        </span>
                        <span className="history-timeline-date">
                            {formatDateTime(item.changed_at)}
                        </span>
                    </div>
                    <div className="history-timeline-fields">
                        {Object.entries(changes).map(([field, value]) => (
                            <div
                                key={field}
                                className="history-timeline-field-row"
                            >
                                <span className="field-label">{field}:</span>
                                <span className="field-value">
                                    {typeof value === 'object'
                                        ? JSON.stringify(value)
                                        : String(value)}
                                </span>
                            </div>
                        ))}
                        {item.changed_by_user_id && (
                            <div className="history-timeline-field-row small">
                                <span className="field-label">
                                    {historyT.changedByUserId}
                                </span>
                                <span className="field-value">
                                    {item.changed_by_user_id}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (loading && availableYears.length === 0) {
        return <Spinner />;
    }

    const yearOptions = availableYears.map(year => ({
        value: year,
        label: `${year}`
    }));
    const selectedYearOption = yearOptions.find(
        option => option.value === selectedYear
    );

    const comparisonFieldOptions = Object.entries(historyFieldLabels).map(
        ([key, label]) => ({
            value: key,
            label
        })
    );

    const selectedComparisonYearOptions = availableYears
        .filter(year => selectedComparisonYears.includes(year))
        .map(year => ({ value: year, label: `${year}` }));

    const selectedComparisonFieldOptions = Object.entries(historyFieldLabels)
        .filter(([key]) => selectedComparisonFields.includes(key))
        .map(([key, label]) => ({ value: key, label }));

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: 'rgba(8, 24, 45, 0.95)',
            borderColor: state.isFocused
                ? '#00C0C7'
                : 'rgba(148,163,184,0.35)',
            boxShadow: state.isFocused
                ? '0 0 0 1px rgba(0,192,199,0.6)'
                : 'none',
            '&:hover': {
                borderColor: '#00C0C7'
            },
            color: '#E5E7EB',
            minHeight: 40,
            borderRadius: 999,
            cursor: 'pointer',
            fontSize:
                'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }),
        singleValue: provided => ({
            ...provided,
            color: '#E5E7EB'
        }),
        multiValue: provided => ({
            ...provided,
            backgroundColor: 'rgba(15,23,42,0.9)'
        }),
        multiValueLabel: provided => ({
            ...provided,
            color: '#E5E7EB',
            fontSize:
                'calc(var(--ui-font-size) * var(--font-size-multiplier))'
        }),
        multiValueRemove: provided => ({
            ...provided,
            color: '#94A3B8',
            '&:hover': {
                backgroundColor: '#EF4444',
                color: '#FFF'
            }
        }),
        input: provided => ({
            ...provided,
            color: '#E5E7EB'
        }),
        placeholder: provided => ({
            ...provided,
            color: 'rgba(148,163,184,0.9)'
        }),
        menu: provided => ({
            ...provided,
            backgroundColor: '#020617',
            borderRadius: 12,
            border: '1px solid rgba(148,163,184,0.4)',
            overflow: 'hidden',
            zIndex: 9999
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#00C0C7'
                : state.isFocused
                ? 'rgba(30,64,175,0.5)'
                : '#020617',
            color: state.isSelected ? '#02131F' : '#E5E7EB',
            cursor: 'pointer',
            fontSize:
                'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }),
        dropdownIndicator: provided => ({
            ...provided,
            color: '#94A3B8',
            '&:hover': { color: '#00C0C7' }
        }),
        clearIndicator: provided => ({
            ...provided,
            color: '#94A3B8',
            '&:hover': { color: '#00C0C7' }
        }),
        indicatorSeparator: () => ({ display: 'none' })
    };

    const power =
        assetSnapshot?.OperationalData?.power !== undefined &&
        assetSnapshot?.OperationalData?.power !== null
            ? assetSnapshot.OperationalData.power
            : '-';
    const voltage =
        assetSnapshot?.OperationalData?.voltage !== undefined &&
        assetSnapshot?.OperationalData?.voltage !== null
            ? assetSnapshot.OperationalData.voltage
            : '-';
    const efficiency =
        assetSnapshot?.OperationalData?.efficiency_percent !== undefined &&
        assetSnapshot?.OperationalData?.efficiency_percent !== null
            ? assetSnapshot.OperationalData.efficiency_percent
            : '-';
    const hours =
        assetSnapshot?.OperationalData?.running_hours_counter !== undefined &&
        assetSnapshot?.OperationalData?.running_hours_counter !== null
            ? assetSnapshot.OperationalData.running_hours_counter
            : '-';

    return (
        <div
            className=" configuration"
            style={{ direction: isRtl ? 'rtl' : 'ltr' }}
        >
            <Alert
                open={alertState.open}
                close={() =>
                    setAlertState({ ...alertState, open: false })
                }
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />

            {/* top chrome similar to app layout */}
            {/* compact header like Asset Details / Figma */}
            <div className="history-page-header">
                <div className="history-page-header-left">
                    <button
                        className="history-back-btn-circle"
                        onClick={() => navigate(-1)}
                        aria-label={historyT.backAria}
                    >
                        &larr;
                    </button>

                    <div className="history-page-title-wrap">
                        <div className="history-page-title">
                            {`${historyT.titlePrefix} ${
                                assetSnapshot?.asset_name ||
                                `${historyT.assetIdFallback} ${assetId}`
                            }`}
                        </div>
                        <div className="history-page-subtitle">
                            {historyT.subtitle}
                        </div>
                    </div>
                </div>

                <div className="history-page-header-right">
                    <div className="history-filter-chip">
                        <span className="filter-label">
                            {historyT.filterByYear}
                        </span>
                        <div className="filter-select-wrapper">
                            <Select
                                options={yearOptions}
                                value={selectedYearOption}
                                onChange={handleYearChange}
                                styles={customSelectStyles}
                                classNamePrefix="react-select"
                            />
                        </div>
                    </div>

                    <button
                        className="devices-primary-btn"
                        onClick={() => {
                            setShowComparisonMode(true);
                            setShowAllHistoryMode(false);
                            setSelectedComparisonYears(
                                availableYears.filter(y => y <= currentYear)
                            );
                            setSelectedComparisonFields(
                                Object.keys(historyTrackedFields)
                            );
                        }}
                    >
                        {historyT.compare}
                    </button>

                    <button
                        className="history-close-btn-icon"
                        onClick={() => navigate('/asset-card')}
                        aria-label={historyT.closeAria}
                    >
                        &times;
                    </button>
                </div>
            </div>


            {/* main panel */}
            <div className="asset-main-panel history-main-panel">
                {loading ? (
                    <Spinner />
                ) : showComparisonMode ? (
                    <div className="history-comparison-section">
                        <div className="comparison-header">
                            <h2>{historyT.comparisonTitle}</h2>
                            <button
                                className="ghost-btn"
                                onClick={handleBackToSnapshot}
                            >
                                &larr; {addAssetT.buttonBack}
                            </button>
                        </div>

                        <div className="comparison-controls">
                            <div className="control-block">
                                <label className="control-label">
                                    {historyT.selectYears}
                                </label>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    options={yearOptions}
                                    value={selectedComparisonYearOptions}
                                    onChange={handleComparisonYearChange}
                                    styles={customSelectStyles}
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div className="control-block">
                                <label className="control-label">
                                    {historyT.selectFields}
                                </label>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    options={comparisonFieldOptions}
                                    value={selectedComparisonFieldOptions}
                                    onChange={handleComparisonFieldChange}
                                    styles={customSelectStyles}
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div className="control-actions">
                                <button
                                    className="primary-btn"
                                    onClick={fetchComparisonData}
                                >
                                    {historyT.generateComparison}
                                </button>
                                <button
                                    className="ghost-btn"
                                    onClick={handleBackToSnapshot}
                                >
                                    {historyT.cancel}
                                </button>
                            </div>
                        </div>

                        {comparisonData.length > 0 &&
                        selectedComparisonFields.length > 0 ? (
                            <div className="comparison-table-wrapper">
                                <table className="comparison-table">
                                    <thead>
                                        <tr>
                                            <th>{historyT.fieldColumn}</th>
                                            {selectedComparisonYears
                                                .slice()
                                                .sort((a, b) => a - b)
                                                .map(year => (
                                                    <th key={year}>
                                                        {year}
                                                    </th>
                                                ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedComparisonFields.map(
                                            fieldKey => (
                                                <tr key={fieldKey}>
                                                    <td>
                                                        <strong>
                                                            {
                                                                historyFieldLabels[
                                                                    fieldKey
                                                                ] || fieldKey
                                                            }
                                                        </strong>
                                                    </td>
                                                    {selectedComparisonYears
                                                        .slice()
                                                        .sort(
                                                            (a, b) => a - b
                                                        )
                                                        .map(year => {
                                                            const dataForYear =
                                                                comparisonData.find(
                                                                    d =>
                                                                        d.year ===
                                                                        year
                                                                );
                                                            let value =
                                                                dataForYear
                                                                    ? dataForYear[
                                                                          fieldKey
                                                                      ]
                                                                    : '-';
                                                            if (
                                                                value instanceof
                                                                Date
                                                            ) {
                                                                value =
                                                                    moment(
                                                                        value
                                                                    ).format(
                                                                        'DD/MM/YYYY'
                                                                    );
                                                            } else if (
                                                                typeof value ===
                                                                    'object' &&
                                                                value !== null
                                                            ) {
                                                                value =
                                                                    JSON.stringify(
                                                                        value
                                                                    );
                                                            }
                                                            return (
                                                                <td
                                                                    key={`${fieldKey}-${year}`}
                                                                >
                                                                    {value ===
                                                                    null
                                                                        ? '-'
                                                                        : String(
                                                                              value
                                                                          )}
                                                                </td>
                                                            );
                                                        })}
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="history-empty-text">
                                {historyT.comparisonEmpty}
                            </p>
                        )}
                    </div>
                ) : showAllHistoryMode ? (
                    <div className="history-all-records-section">
                        <div className="section-header-row">
                            <h2>
                                {interpolateText(historyT.allHistoryTitle, {
                                    year: selectedYear
                                })}
                            </h2>
                            <button
                                className="ghost-btn"
                                onClick={handleBackToSnapshot}
                            >
                                &larr; {historyT.backToOverview}
                            </button>
                        </div>

                        {combinedHistoryTimeline().length > 0 ? (
                            <div className="history-timeline">
                                {combinedHistoryTimeline().map(item =>
                                    renderHistoryItem(item)
                                )}
                            </div>
                        ) : (
                            <p className="history-empty-text">
                                {interpolateText(historyT.noDetailedHistory, {
                                    year: selectedYear
                                })}
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="history-snapshot-section history-snapshot-v2">
                        <div className="history-overview-card">
                            <div className="history-overview-head">
                                <div className="history-overview-left">
                                    <div className="history-overview-icon">
                                        <AssetCardMainIcon />
                                    </div>
                                    <div className="history-overview-text">
                                        <div className="history-overview-title">
                                            {interpolateText(
                                                isCurrentYearLive
                                                    ? historyT.profileTitleLive
                                                    : historyT.profileTitleYear,
                                                { year: selectedYear }
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    className="devices-primary-btn"
                                    onClick={() => fetchAllHistoryRecordsForYear(selectedYear)}
                                >
                                    {historyT.viewAllHistory}
                                </button>
                            </div>

                            <div className="history-metrics-row history-metrics-row-v2">
                                <div className="history-metric-card">
                                    <div className="metric-icon metric-power">
                                        <PowerIcon />
                                    </div>
                                    <div className="metric-text">
                                        <span className="metric-label">
                                            {historyT.avgPower}
                                        </span>
                                        <span className="metric-value">
                                            {power}{' '}{power !== '-' ? 'kW' : ''}
                                        </span>
                                    </div>
                                </div>

                                <div className="history-metric-card">
                                    <div className="metric-icon metric-voltage">
                                        <VoltageIcon />
                                    </div>
                                    <div className="metric-text">
                                        <span className="metric-label">
                                            {historyT.avgVoltage}
                                        </span>
                                        <span className="metric-value">
                                            {voltage}{' '}{voltage !== '-' ? 'V' : ''}
                                        </span>
                                    </div>
                                </div>

                                <div className="history-metric-card">
                                    <div className="metric-icon metric-eff">
                                        <EfficiencyIcon />
                                    </div>
                                    <div className="metric-text">
                                        <span className="metric-label">
                                            {historyT.efficiency}
                                        </span>
                                        <span className="metric-value">
                                            {efficiency}{efficiency !== '-' ? '%' : ''}
                                        </span>
                                    </div>
                                </div>

                                <div className="history-metric-card">
                                    <div className="metric-icon metric-hours">
                                        <span className="hours-dot" />
                                    </div>
                                    <div className="metric-text">
                                        <span className="metric-label">
                                            {historyT.totalHours}
                                        </span>
                                        <span className="metric-value">
                                            {hours}{' '}
                                            {hours !== '-' ? historyT.hoursUnit : ''}
                                        </span>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {assetSnapshot ? (
                            <div className="history-details-panel-v2">
                                <div className="details-body-grid">
                                    {/* IDENTIFICATION */}
                                    <section className="history-section-card">
                                        <header className="history-section-header">
                                            <div className="history-section-title">
                                                {addAssetT.step1.title}
                                            </div>
                                        </header>
                                        <div className="history-section-grid">
                                            <div className="history-field-item">
                                                <span className="field-label">
                                                    {assetT.fieldName}
                                                </span>
                                                <span className="field-value">
                                                    {
                                                        assetSnapshot.asset_name
                                                    }
                                                </span>
                                            </div>
                                            <div className="history-field-item">
                                                <span className="field-label">
                                                    {assetT.fieldCode}
                                                </span>
                                                <span className="field-value">
                                                    {
                                                        assetSnapshot.asset_code
                                                    }
                                                </span>
                                            </div>
                                            <div className="history-field-item">
                                                <span className="field-label">
                                                    {
                                                        assetT.fieldCategorySubcategory
                                                    }
                                                </span>
                                                <span className="field-value">
                                                    {
                                                        assetSnapshot.category_subcategory
                                                    }
                                                </span>
                                            </div>
                                            <div className="history-field-item">
                                                <span className="field-label">
                                                    {assetT.fieldBrand}
                                                </span>
                                                <span className="field-value">
                                                    {assetSnapshot.brand}
                                                </span>
                                            </div>
                                            <div className="history-field-item">
                                                <span className="field-label">
                                                    {
                                                        assetT.fieldModelNumber
                                                    }
                                                </span>
                                                <span className="field-value">
                                                    {
                                                        assetSnapshot.model_number
                                                    }
                                                </span>
                                            </div>
                                            <div className="history-field-item">
                                                <span className="field-label">
                                                    {
                                                        assetT.fieldSerialNumber
                                                    }
                                                </span>
                                                <span className="field-value">
                                                    {
                                                        assetSnapshot.serial_number
                                                    }
                                                </span>
                                            </div>
                                            <div className="history-field-item">
                                                <span className="field-label">
                                                    {assetT.fieldType}
                                                </span>
                                                <span className="field-value">
                                                    {assetSnapshot.type}
                                                </span>
                                            </div>
                                            <div className="history-field-item">
                                                <span className="field-label">
                                                    {
                                                        assetT.fieldManufacturer
                                                    }
                                                </span>
                                                <span className="field-value">
                                                    {
                                                        assetSnapshot.manufacturer
                                                    }
                                                </span>
                                            </div>
                                            <div className="history-field-item">
                                                <span className="field-label">
                                                    {
                                                        assetT.fieldManufactureDate
                                                    }
                                                </span>
                                                <span className="field-value">
                                                    {assetSnapshot.manufacture_date
                                                        ? moment(
                                                              assetSnapshot.manufacture_date
                                                          ).format(
                                                              'DD/MM/YYYY'
                                                          )
                                                        : '-'}
                                                </span>
                                            </div>
                                            <div className="history-field-item">
                                                <span className="field-label">
                                                    {assetT.fieldLocation}
                                                </span>
                                                <span className="field-value">
                                                    {assetSnapshot.location}
                                                </span>
                                            </div>
                                        </div>
                                    </section>

                                    {/* OPERATIONAL */}
                                    {assetSnapshot.OperationalData && (
                                        <section className="history-section-card">
                                            <header className="history-section-header">
                                                <div className="history-section-title">
                                                    {assetT.operationalDataTitle}
                                                </div>
                                            </header>
                                            <div className="history-section-grid">
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldCapacityRating
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .OperationalData
                                                                .capacity_rating
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {assetT.fieldPower}
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .OperationalData
                                                                .power
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {assetT.fieldVoltage}
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .OperationalData
                                                                .voltage
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {assetT.fieldCurrent}
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .OperationalData
                                                                .current
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldConnectionType
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .OperationalData
                                                                .connection_type
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldEfficiencyPercent
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .OperationalData
                                                                .efficiency_percent
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldRunningHoursCounter
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .OperationalData
                                                                .running_hours_counter
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </section>
                                    )}

                                    {/* MAINTENANCE */}
                                    {assetSnapshot.MaintenanceLifecycle && (
                                        <section className="history-section-card">
                                            <header className="history-section-header">
                                                <div className="history-section-title">
                                                    {assetT.maintenanceLifecycleTitle}
                                                </div>
                                            </header>
                                            <div className="history-section-grid">
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldInstallationDate
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {assetSnapshot
                                                            .MaintenanceLifecycle
                                                            .installation_date
                                                            ? moment(
                                                                  assetSnapshot
                                                                      .MaintenanceLifecycle
                                                                      .installation_date
                                                              ).format(
                                                                  'DD/MM/YYYY'
                                                              )
                                                            : '-'}
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldWarrantyExpiryDate
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {assetSnapshot
                                                            .MaintenanceLifecycle
                                                            .warranty_expiry_date
                                                            ? moment(
                                                                  assetSnapshot
                                                                      .MaintenanceLifecycle
                                                                      .warranty_expiry_date
                                                              ).format(
                                                                  'DD/MM/YYYY'
                                                              )
                                                            : '-'}
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldConditionStatus
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .MaintenanceLifecycle
                                                                .condition_status
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldTechnicalEvaluation
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .MaintenanceLifecycle
                                                                .technical_evaluation
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldCriticalityLevel
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .MaintenanceLifecycle
                                                                .criticality_level
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldEstimatedLifeCycleYears
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .MaintenanceLifecycle
                                                                .estimated_life_cycle_years
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldLastMaintenanceDate
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {assetSnapshot
                                                            .MaintenanceLifecycle
                                                            .last_maintenance_date
                                                            ? moment(
                                                                  assetSnapshot
                                                                      .MaintenanceLifecycle
                                                                      .last_maintenance_date
                                                              ).format(
                                                                  'DD/MM/YYYY'
                                                              )
                                                            : '-'}
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldNextMaintenanceDue
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {assetSnapshot
                                                            .MaintenanceLifecycle
                                                            .next_maintenance_due
                                                            ? moment(
                                                                  assetSnapshot
                                                                      .MaintenanceLifecycle
                                                                      .next_maintenance_due
                                                              ).format(
                                                                  'DD/MM/YYYY'
                                                              )
                                                            : '-'}
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldOAndMVendor
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .MaintenanceLifecycle
                                                                .o_and_m_vendor
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </section>
                                    )}

                                    {/* FINANCIAL */}
                                    {assetSnapshot.FinancialOwnership && (
                                        <section className="history-section-card">
                                            <header className="history-section-header">
                                                <div className="history-section-title">
                                                    {assetT.financialOwnershipTitle}
                                                </div>
                                            </header>
                                            <div className="history-section-grid">
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldSupplierVendor
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .FinancialOwnership
                                                                .supplier_vendor
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldDepartmentOwner
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .FinancialOwnership
                                                                .department_owner
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldPurchaseCost
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {assetSnapshot
                                                            .FinancialOwnership
                                                            .purchase_cost
                                                            ? parseFloat(
                                                                  assetSnapshot
                                                                      .FinancialOwnership
                                                                      .purchase_cost
                                                              ).toFixed(2)
                                                            : '-'}
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldAnnualOmCost
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {assetSnapshot
                                                            .FinancialOwnership
                                                            .annual_om_cost
                                                            ? parseFloat(
                                                                  assetSnapshot
                                                                      .FinancialOwnership
                                                                      .annual_om_cost
                                                              ).toFixed(2)
                                                            : '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        </section>
                                    )}

                                    {/* IOT */}
                                    {assetSnapshot.IotMonitoring && (
                                        <section className="history-section-card">
                                            <header className="history-section-header">
                                                <div className="history-section-title">
                                                    {assetT.iotMonitoringTitle}
                                                </div>
                                            </header>
                                            <div className="history-section-grid">
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldMonitoredParameters
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .IotMonitoring
                                                                .monitored_parameters
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldAlertThresholds
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .IotMonitoring
                                                                .alert_thresholds
                                                        }
                                                    </span>
                                                </div>
                                                <div className="history-field-item">
                                                    <span className="field-label">
                                                        {
                                                            assetT.fieldIntegrationProtocol
                                                        }
                                                    </span>
                                                    <span className="field-value">
                                                        {
                                                            assetSnapshot
                                                                .IotMonitoring
                                                                .integration_protocol
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </section>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <p className="history-empty-text">
                                {interpolateText(historyT.noAssetDataForYear, {
                                    year: selectedYear
                                })}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
